import { useTranslation } from "react-i18next";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import ToolGrid from "../components/ToolGrid";
import { useAuth } from "../contexts/AuthContext";

export default function Landing() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  // Si ya hay sesión iniciada, saltamos la landing y vamos directos al panel.
  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;

  return (
    <div className="app-shell" data-testid="landing-page">
      {/* Hero */}
      <section className="relative overflow-hidden border-b-2 border-[#1C1917]">
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.18] pointer-events-none"
          style={{
            backgroundImage:
              "url('https://static.prod-images.emergentagent.com/jobs/53b1a0c1-279a-40d8-8619-21a76576d034/images/9e4baf036ffad9f8978a69834f144d4f06934e49d4bc812859d4787f5d5ad624.png')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8 fade-up">
            <span className="sticker" data-testid="hero-eyebrow">{t("hero_eyebrow")} · v1.0</span>
            <h1 className="display mt-6 text-5xl sm:text-6xl lg:text-8xl">
              {t("hero_title_1")}
              <br />
              <span className="text-[#E5484D]">{t("hero_title_2")}</span>
            </h1>
            <p className="mt-8 text-lg sm:text-xl text-[#57534E] max-w-2xl font-light leading-relaxed">
              {t("hero_sub")}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Button
                className="btn-primary rounded-none h-12 px-8 text-sm tracking-widest uppercase"
                onClick={() => navigate("/register")}
                data-testid="hero-cta-start"
              >
                {t("cta_start")} →
              </Button>
              <Button
                variant="outline"
                className="rounded-none border-2 border-[#1C1917] bg-white hover:bg-[#FFC53D] h-12 px-8 text-sm tracking-widest uppercase font-bold shadow-[4px_4px_0_0_#1C1917] hover:shadow-[5px_5px_0_0_#1C1917] hover:-translate-y-[1px] active:translate-y-[3px] active:shadow-none transition-all"
                onClick={() => navigate("/login")}
                data-testid="hero-cta-login"
              >
                {t("cta_login")}
              </Button>
            </div>
          </div>
          <div className="lg:col-span-4 fade-up fade-up-delay-2">
            <div className="border-2 border-[#1C1917] shadow-[6px_6px_0_0_#1C1917] bg-white p-6">
              <div className="eyebrow">Index</div>
              <div className="mt-4 grid grid-cols-2 gap-4">
                <div>
                  <div className="display text-3xl text-[#1C1917]">17+</div>
                  <div className="text-[10px] tracking-wider uppercase text-[#57534E] mt-1 font-semibold">
                    {t("stat_formats")}
                  </div>
                </div>
                <div>
                  <div className="display text-3xl text-[#E5484D]">14</div>
                  <div className="text-[10px] tracking-wider uppercase text-[#57534E] mt-1 font-semibold">
                    {t("stat_tools")}
                  </div>
                </div>
                <div>
                  <div className="display text-3xl text-[#7C3AED]">100+</div>
                  <div className="text-[10px] tracking-wider uppercase text-[#57534E] mt-1 font-semibold">
                    {t("stat_templates")}
                  </div>
                </div>
                <div>
                  <div className="display text-3xl text-[#3E63DD]">100%</div>
                  <div className="text-[10px] tracking-wider uppercase text-[#57534E] mt-1 font-semibold">
                    {t("stat_secure")}
                  </div>
                </div>
              </div>
              <div className="h-[2px] bg-[#1C1917] mt-6 mb-4" />
              <div className="text-sm text-[#57534E] font-light leading-relaxed">
                {t("keep_history")}
              </div>
            </div>
          </div>
        </div>

        {/* Marquee tape with formats */}
        <div className="border-t-2 border-[#1C1917] bg-[#1C1917] overflow-hidden">
          <div className="marquee-track flex whitespace-nowrap py-3">
            {Array.from({ length: 2 }).flatMap((_, k) =>
              ["PDF", "DOCX", "ODT", "RTF", "XLSX", "XLS", "ODS", "CSV", "PPTX", "ODP", "HTML", "TXT", "MD", "PNG", "JPG", "WEBP", "TIFF"].map((f, i) => (
                <span key={`${k}-${i}`} className="px-6 eyebrow text-[#FDFBF7]">
                  {f} <span className="text-[#FFC53D] mx-2">/</span>
                </span>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Document Generator showcase */}
      <section className="border-b-2 border-[#1C1917] bg-[#FFE5A0]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <span className="sticker bg-[#7C3AED] text-white" data-testid="docgen-eyebrow">
              {t("docgen_landing_eyebrow")}
            </span>
            <h2 className="display mt-6 text-4xl sm:text-5xl lg:text-6xl text-[#1C1917]">
              {t("docgen_landing_title_1")}{" "}
              <span className="text-[#7C3AED]">{t("docgen_landing_title_2")}</span>
            </h2>
            <p className="mt-6 text-base sm:text-lg text-[#1C1917] max-w-2xl leading-relaxed">
              {t("docgen_landing_sub")}
            </p>
            <div className="mt-8 grid sm:grid-cols-2 gap-3 max-w-2xl">
              {[
                "docgen_landing_bullet_1",
                "docgen_landing_bullet_2",
                "docgen_landing_bullet_3",
                "docgen_landing_bullet_4",
              ].map((k) => (
                <div key={k} className="flex items-start gap-3 text-sm text-[#1C1917]">
                  <span className="mt-0.5 inline-grid place-items-center w-5 h-5 bg-[#1C1917] text-[#FFE5A0] font-black text-[11px] border-2 border-[#1C1917]">✓</span>
                  <span>{t(k)}</span>
                </div>
              ))}
            </div>
            <div className="mt-10 flex flex-wrap gap-4">
              <Button
                onClick={() => navigate("/register")}
                className="rounded-none h-12 px-8 text-sm tracking-widest uppercase border-2 border-[#1C1917] bg-[#1C1917] text-[#FDFBF7] hover:bg-black font-black shadow-[4px_4px_0_0_#1C1917] hover:shadow-[6px_6px_0_0_#1C1917] hover:-translate-y-[1px] active:translate-y-[3px] active:shadow-none transition-all"
                data-testid="docgen-cta-trial"
              >
                {t("docgen_landing_cta")} →
              </Button>
              <Link
                to="/tools/docgen"
                className="inline-flex items-center h-12 px-6 text-sm tracking-widest uppercase font-black text-[#1C1917] underline underline-offset-4 decoration-2 hover:text-[#7C3AED]"
                data-testid="docgen-explore"
              >
                {t("docgen_landing_explore")}
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="grid grid-cols-2 gap-3" data-testid="docgen-categories-preview">
              {[
                { k: "rrhh", count: 25, color: "#E5484D" },
                { k: "comunicaciones", count: 15, color: "#FFC53D" },
                { k: "contratos", count: 15, color: "#3E63DD" },
                { k: "legal", count: 12, color: "#7C3AED" },
                { k: "comercial", count: 10, color: "#16A34A" },
                { k: "finanzas", count: 10, color: "#0EA5E9" },
                { k: "actas", count: 8, color: "#F97316" },
                { k: "cartas", count: 5, color: "#1C1917" },
              ].map((c) => (
                <div
                  key={c.k}
                  className="border-2 border-[#1C1917] bg-white p-4 shadow-[3px_3px_0_0_#1C1917] hover:shadow-[5px_5px_0_0_#1C1917] hover:-translate-y-[1px] transition-all"
                >
                  <div
                    className="w-8 h-1.5 mb-3"
                    style={{ background: c.color }}
                  />
                  <div className="text-xs uppercase tracking-wider font-bold text-[#1C1917]">
                    {t(`docgen_cat_${c.k}`)}
                  </div>
                  <div className="display text-3xl mt-1 text-[#1C1917]">{c.count}</div>
                </div>
              ))}
            </div>
            <p className="mt-4 text-xs uppercase tracking-widest font-bold text-[#1C1917] text-center">
              {t("docgen_landing_total")}
            </p>
          </div>
        </div>
      </section>

      {/* Tools */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
          <div>
            <span className="eyebrow">02 — {t("tools_title")}</span>
            <h2 className="hero-h2 text-3xl sm:text-5xl mt-3">{t("tools_title")}</h2>
            <p className="text-zinc-600 mt-2 max-w-xl font-light">{t("tools_subtitle")}</p>
          </div>
          <Link
            to="/tools/convert"
            className="text-sm tracking-wider uppercase font-medium underline underline-offset-4 hover:text-zinc-500"
            data-testid="link-all-tools"
          >
            → {t("nav_convert")}
          </Link>
        </div>
        <ToolGrid />
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-200 py-10 mt-10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex justify-between items-center flex-wrap gap-3 text-xs text-zinc-500">
          <span>© {new Date().getFullYear()} DocuConvert. {t("footer_made")}</span>
          <span className="eyebrow">DocuConvert / V1.0</span>
        </div>
      </footer>
    </div>
  );
}
